import { Link } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";
import FadeIn from "@/components/FadeIn";
import SeoHead from "@/components/SeoHead";
import InzichtenLinkBlock from "@/components/InzichtenLinkBlock";
import InternalLinkBlock from "@/components/InternalLinkBlock";
import { SERVICES } from "@/data/services";

const decisionGuide = [
  {
    situation:
      "Nog geen compleet overzicht van AI gebruik, cloudservices en datastromen",
    advies: "Bestuursscan AI en Data",
    href: "#bestuursscan",
  },
  {
    situation:
      "Behoefte aan diepgang, prioritering en een volledig rapport",
    advies: "Regiebeeld AI, Cloud en Data",
    href: "#regiebeeld",
  },
  {
    situation:
      "Inzicht aanwezig, maar beleid, registers en governance ontbreken",
    advies: "Regiekader AI en Datagovernance",
    href: "#regiekader",
  },
  {
    situation:
      "Governance ingericht, maar structurele actualisatie gewenst",
    advies: "RegieBorging",
    href: "#regieborging",
  },
];

const services = [
  {
    id: "bestuursscan",
    badge: "INSTAP",
    title: "Bestuursscan AI en Data",
    subtitle: "Eerste bestuurlijke risicofoto",
    description:
      "De Bestuursscan is het startpunt voor organisaties die snel willen weten waar de grootste bestuurlijke risico's zitten rond AI gebruik, cloudafhankelijkheden, algoritmen en gevoelige data. De scan geeft geen technisch detailrapport, maar een helder bestuurlijk beeld dat gebruikt kan worden voor interne prioritering en besluitvorming.",
    includes: [
      "Intakegesprek met sleutelpersonen",
      "Inventarisatie van AI tools, cloudservices, algoritmen en kritieke datasystemen",
      "Beoordeling op de acht dimensies van de SovEU Regiemethode",
      "Eerste duiding van datastromen, toegangsrechten en leveranciersafhankelijkheden",
      "Top vijf bestuurlijke risico's",
      "Actielijst voor de korte termijn",
      "Aanbeveling voor vervolgstap",
    ],
    audience:
      "Gemeenten, woningcorporaties, onderwijsinstellingen, GGD'en, veiligheidsregio's, adviesorganisaties en semipublieke instellingen die snel overzicht willen.",
    cta: "Plan een gesprek over de Bestuursscan",
  },
  {
    id: "regiebeeld",
    badge: "VERDIEPING",
    title: "Regiebeeld AI, Cloud en Data",
    subtitle: "Volledig bestuurlijk overzicht",
    description:
      "Het Regiebeeld is een diepgaande analyse van AI gebruik, cloudafhankelijkheid, algoritmen, datastromen en governance. Het resultaat is een rapport dat direct bruikbaar is voor bestuur, directie, management, CIO, CISO, juridische afdeling en toezichthouders.",
    includes: [
      "Documentanalyse",
      "Interviews met relevante sleutelpersonen",
      "Inventarisatie van AI toepassingen, algoritmen, cloudservices en datasystemen",
      "Jurisdictie en leveranciersanalyse",
      "Analyse van datastromen en toegangsrechten",
      "Duiding van relevante kaders zoals AI verordening, AVG, BIO, NIS2 of DORA waar relevant",
      "Analyse van algoritmeregister en AI register",
      "Bestuurlijke risicomatrix",
      "Actieplan voor de komende periode",
      "Presentatie aan directie, bestuur of management",
      "Optioneel verdiepingsblok over proportionaliteit en digitale voetafdruk",
    ],
    audience:
      "Organisaties die niet alleen signalen willen, maar een volledig bestuurlijk overzicht en prioritering.",
    cta: "Plan een gesprek over het Regiebeeld",
  },
  {
    id: "regiekader",
    badge: "INRICHTING",
    title: "Regiekader AI en Datagovernance",
    subtitle: "Governance daadwerkelijk inrichten",
    description:
      "Het Regiekader helpt organisaties om van inzicht naar inrichting te komen. Beleid, registers en rollen worden samen met de organisatie ontworpen en vastgesteld, zodat governance daadwerkelijk werkt in de praktijk en bestuurlijk kan worden uitgelegd.",
    includes: [
      "AI beleid en cloudbeleid afgestemd op het bestaande informatiebeleid",
      "Dataclassificatie en bewaarkaders",
      "Inrichting van een AI register en algoritmeregister",
      "Leveranciersbeoordeling en jurisdictie kader",
      "Rollen, verantwoordelijkheden en bestuurlijk eigenaarschap",
      "Bestuurlijke incidentvoorbereiding en draaiboek",
      "Begeleiding bij vaststelling in directie of bestuur",
    ],
    audience:
      "Organisaties die de stap willen maken van overzicht naar werkbare governance: gemeenten, uitvoeringsorganisaties, woningcorporaties, onderwijsinstellingen en maatschappelijke organisaties.",
    cta: "Plan een gesprek over het Regiekader",
  },
  {
    id: "regieborging",
    badge: "STRUCTUREEL",
    title: "RegieBorging",
    subtitle: "Structureel actueel en aantoonbaar in control",
    description:
      "Doorlopende borging van governance voor bestaande klanten. Nieuwe ontwikkelingen, nieuwe tools, nieuwe risico's en nieuwe vragen worden periodiek beoordeeld zodat de organisatie actueel en aantoonbaar in control blijft. Geen losse opdrachten meer, maar een vaste ritmiek van regie.",
    includes: [
      "Periodieke herijking van de bestuurlijke risicofoto",
      "Actualisatie van AI register en algoritmeregister",
      "Periodieke beleidsreview",
      "Vast SovEU aanspreekpunt voor bestuurlijke vragen",
      "Korte ondersteuning bij AI of datagerelateerde vraagstukken",
    ],
    audience:
      "Organisaties die eerder een Regiebeeld of Regiekader hebben afgerond en grip op AI, cloud en data structureel willen borgen.",
    cta: "Plan een gesprek over RegieBorging",
  },
];

const aiActServices = [
  {
    id: "ai-act-startcheck",
    badge: "INSTAP",
    title: "AI Act Startcheck",
    subtitle: "Eerste laagdrempelige beoordeling",
    description:
      "Een laagdrempelige eerste beoordeling voor organisaties die willen weten of hun AI-gebruik relevant is voor de AI Act, AVG, AI-geletterdheid en interne governance. Geschikt voor bedrijven, MKB-bedrijven en organisaties die nog geen centraal overzicht hebben van AI-tools en toepassingen.",
    includes: [
      "Eerste inventarisatie van AI-gebruik",
      "Indicatie van relevante AI-risico's",
      "Eerste beoordeling van AI-geletterdheid",
      "Signalen rond AVG, data en interne beheersing",
      "Praktisch overzicht van vervolgstappen",
    ],
    cta: "Plan een kennismaking",
  },
  {
    id: "ai-act-quickscan",
    badge: "SCAN",
    title: "AI Act QuickScan",
    subtitle: "Gestructureerd beeld van AI-gebruik en risico's",
    description:
      "Een gestructureerde scan voor organisaties die hun AI-gebruik, risico's en documentatie beter willen begrijpen. De QuickScan geeft directie, management, FG/DPO, CISO of compliance een praktisch beeld van waar de organisatie staat.",
    includes: [
      "AI-use inventarisatie",
      "Risicoclassificatie per toepassing",
      "AI-geletterdheid gap-analyse",
      "Data- en governance-risico's",
      "Governance-aandachtspunten",
      "Managementsamenvatting en actielijst",
    ],
    cta: "Vraag de QuickScan aan",
  },
  {
    id: "ai-act-readiness-sprint",
    badge: "READINESS",
    title: "AI Act Readiness Sprint",
    subtitle: "Van losse scan naar bruikbaar AI-dossier",
    description:
      "Voor organisaties die meer nodig hebben dan een losse scan. De AI Act Readiness Sprint helpt om AI-gebruik, risico's, beleid en documentatie samen te brengen in een bruikbaar AI-dossier.",
    includes: [
      "AI-register light",
      "Risicoclassificatie",
      "Concept AI-beleid",
      "Governance-afspraken",
      "Toetsingsvragen voor AI-toepassingen",
      "Human oversight checklist",
      "Transparantiecheck",
      "Directie- of bestuursmemo",
    ],
    cta: "Bespreek de Readiness Sprint",
  },
  {
    id: "ai-governance-implementatie",
    badge: "IMPLEMENTATIE",
    title: "AI Governance Implementatie",
    subtitle: "AI structureel borgen in de bedrijfsvoering",
    description:
      "Voor organisaties die AI structureel willen borgen in beleid, processen, toezicht, inkoop en besluitvorming. Deze dienst helpt organisaties om AI-governance niet als eenmalige exercitie te behandelen, maar als onderdeel van de normale bedrijfsvoering.",
    includes: [
      "AI-governance framework",
      "Proces voor nieuwe AI-use cases",
      "Risico-indeling voor AI-systemen",
      "DPIA/FRIA triggermatrix",
      "Inkoop- en toetsingsvragen",
      "Incidentprocedure",
      "Rapportageformat voor management of bestuur",
    ],
    cta: "Plan een strategisch gesprek",
  },
  {
    id: "white-label",
    badge: "PARTNER",
    title: "White-label AI Act ondersteuning voor adviesbureaus",
    subtitle: "Specialistische partner voor uw klanttrajecten",
    description:
      "Voor privacy-, IT-, HR-, compliance- en organisatieadviesbureaus die klanten krijgen met vragen over AI Act, AI-geletterdheid, AI-governance of AI-risico's. SovEU ondersteunt als specialistische partner of white-label uitvoerder.",
    includes: [
      "Ondersteuning bij klanttrajecten",
      "Templates en methodiek",
      "Analyse en rapportage",
      "AI Act readiness scans",
      "Governance-documentatie",
      "Specialistische sparringpartner",
    ],
    cta: "Plan een partnergesprek",
  },
];

const dienstenFaqs = [
  {
    q: "Moet elk MKB-bedrijf voldoen aan de AI Act?",
    a: "Niet elk MKB-bedrijf heeft dezelfde verplichtingen. De AI Act maakt onderscheid op basis van het soort AI-systeem en het risiconiveau. Bedrijven die AI gebruiken in klantcontact, HR, krediet, software of besluitvoorbereiding doen er goed aan hun AI-gebruik in kaart te brengen.",
  },
  {
    q: "Is ChatGPT of Copilot relevant voor AI-governance?",
    a: "Ja, ook generatieve AI-tools zoals ChatGPT en Microsoft Copilot kunnen relevant zijn voor AI-governance. Organisaties moeten weten waarvoor medewerkers deze tools gebruiken, welke gegevens worden ingevoerd, welke afspraken gelden en hoe verantwoord gebruik wordt geborgd.",
  },
  {
    q: "Wat is AI-geletterdheid?",
    a: "AI-geletterdheid betekent dat medewerkers voldoende kennis hebben om AI-systemen verantwoord te gebruiken. Het gaat niet alleen om training, maar ook om beleid, risico's, toepassing in de praktijk en aantoonbare borging binnen de organisatie.",
  },
  {
    q: "Wat is het verschil tussen AI Act readiness en compliance?",
    a: "AI Act readiness betekent dat een organisatie haar AI-gebruik, risico's en documentatie voorbereidt op relevante verplichtingen. Volledige compliance hangt af van het concrete AI-systeem, de rol van de organisatie en juridische beoordeling. SovEU helpt met voorbereiding, overzicht en governance-documentatie.",
  },
  {
    q: "Geeft SovEU juridisch advies?",
    a: "SovEU geeft geen juridisch keurmerk en belooft geen automatische compliance. SovEU helpt organisaties met praktische analyse, risicoclassificatie, documentatie, governance en vervolgstappen. Waar nodig kan juridische toetsing worden toegevoegd via gespecialiseerde partners.",
  },
];

export default function Diensten() {
  return (
    <div data-testid="page-diensten" className="pt-[96px]">
      <SeoHead
        title="Diensten | AI Act readiness, AI-governance en AI QuickScan | SovEU"
        description="Bekijk de diensten van SovEU voor AI Act readiness, AI-governance, AI QuickScans, AI-geletterdheid, AI-risico's en white-label ondersteuning."
        path="/diensten"
      />
      {/* FAQ structured data */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: dienstenFaqs.map((f) => ({
              "@type": "Question",
              name: f.q,
              acceptedAnswer: { "@type": "Answer", text: f.a },
            })),
          }),
        }}
      />
      {/* Breadcrumb structured data */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Home", item: "https://soveu.nl/" },
              { "@type": "ListItem", position: 2, name: "Diensten", item: "https://soveu.nl/diensten" },
            ],
          }),
        }}
      />
      {/* Hero */}
      <section className="section bg-white">
        <div className="container-soveu">
          <FadeIn className="max-w-3xl">
            <div className="eyebrow mb-4">Aanbod</div>
            <h1 className="h1-display mb-6">
              Diensten voor AI Act readiness, AI-governance en digitale regie
            </h1>
            <div className="space-y-4 body-lg text-[#334155] leading-relaxed">
              <p>
                SovEU ondersteunt organisaties die grip willen krijgen op AI-gebruik, digitale afhankelijkheden, data, algoritmen en AI-risico's. Onze diensten zijn praktisch opgebouwd: van eerste inventarisatie tot governance-inrichting en ondersteuning voor adviesbureaus.
              </p>
              <p>
                Wij beloven geen juridische compliance-stempel en geen automatische naleving. Wij helpen organisaties wel om aantoonbaar in control te komen: wat gebruikt u, waar zitten de risico's, welke documentatie ontbreekt en welke vervolgstappen zijn nodig?
              </p>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* Decision guide */}
      <section className="pb-16" data-testid="diensten-decision-guide">
        <div className="container-soveu">
          <FadeIn>
            <div className="soveu-card p-8 md:p-10">
              <h2 className="h3-display mb-2">Welk product past bij uw situatie?</h2>
              <p className="text-[#6B7280] text-[15px] mb-7">
                Een korte beslisleidraad. Twijfelt u? Plan een vrijblijvend gesprek — dan denken we mee.
              </p>
              <div className="space-y-3">
                {decisionGuide.map((row, i) => (
                  <a
                    key={row.advies}
                    href={row.href}
                    className="decision-row hover:border-[#1E3A5F] transition-colors"
                    data-testid={`decision-row-${i + 1}`}
                  >
                    <div className="flex-1 text-[#334155] text-[15px] leading-relaxed">
                      {row.situation}
                    </div>
                    <ArrowRight size={16} style={{ color: "#2D6F8F" }} className="decision-arrow flex-shrink-0" />
                    <div
                      className="decision-pill flex-shrink-0 px-3 py-1 rounded-full text-[13px] font-medium"
                      style={{ backgroundColor: "#F5F7FA", color: "#1E3A5F" }}
                    >
                      {row.advies}
                    </div>
                  </a>
                ))}
              </div>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* Dienstpagina's — interne links naar detailpagina's */}
      <InternalLinkBlock
        eyebrow="Dienstpagina's"
        title="Dienstpagina's per vraagstuk"
        intro="Elke dienst is uitgewerkt op een eigen pagina, met doelgroep, aanpak, resultaat en veelgestelde vragen."
        items={SERVICES.map((s) => ({
          to: s.to,
          label: s.navLabel,
          summary: s.summary,
        }))}
        columns={2}
        background="#F5F7FA"
        testid="diensten-detailpaginas"
      />

      {/* AI Act readiness en AI-governance diensten */}
      <section
        className="section border-t border-[#E5E9EF] bg-white"
        data-testid="diensten-ai-act-services"
      >
        <div className="container-soveu">
          <FadeIn className="max-w-3xl mb-12">
            <div className="eyebrow mb-3">AI Act readiness en AI-governance</div>
            <h2 className="h2-display mb-5">
              Diensten rond AI Act, AI-governance en verantwoord AI-gebruik
            </h2>
            <p className="body-lg text-[#334155] leading-relaxed">
              Voor bedrijven, MKB, adviesbureaus en publieke organisaties. Van een eerste laagdrempelige beoordeling tot structurele AI-governance en white-label ondersteuning.
            </p>
          </FadeIn>

          <div className="grid md:grid-cols-2 gap-6">
            {aiActServices.map((s, i) => (
              <FadeIn key={s.id} delay={i * 60}>
                <article
                  id={s.id}
                  className="soveu-card p-7 md:p-8 h-full scroll-mt-[110px]"
                  data-testid={`ai-service-${s.id}`}
                >
                  <span
                    className="inline-block text-[11px] font-semibold tracking-[0.14em] uppercase px-2.5 py-1 rounded mb-4"
                    style={{
                      backgroundColor: "#F5F7FA",
                      color: "#2D6F8F",
                      border: "1px solid #E5E9EF",
                    }}
                  >
                    {s.badge}
                  </span>
                  <h3 className="h3-display mb-2">{s.title}</h3>
                  <p
                    className="text-[14px] font-medium mb-4"
                    style={{ color: "#2D6F8F" }}
                  >
                    {s.subtitle}
                  </p>
                  <p className="text-[#334155] leading-relaxed text-[15px] mb-5">
                    {s.description}
                  </p>
                  <ul className="space-y-2 mb-6">
                    {s.includes.map((inc) => (
                      <li
                        key={inc}
                        className="flex items-start gap-2.5 text-[#334155] text-[14px]"
                      >
                        <Check
                          size={16}
                          className="mt-0.5 flex-shrink-0"
                          style={{ color: "#2D6F8F" }}
                          strokeWidth={2}
                        />
                        <span>{inc}</span>
                      </li>
                    ))}
                  </ul>
                  <Link
                    to="/contact"
                    className="inline-flex items-center gap-1.5 text-[14px] font-semibold hover:underline underline-offset-4 transition-colors"
                    style={{ color: "#2D6F8F" }}
                    data-testid={`ai-service-cta-${s.id}`}
                  >
                    {s.cta}
                    <ArrowRight size={15} />
                  </Link>
                </article>
              </FadeIn>
            ))}
          </div>

          <FadeIn delay={200} className="max-w-3xl mt-10">
            <p
              className="text-[14px] leading-relaxed p-5 rounded-md border-l-4"
              style={{
                color: "#334155",
                backgroundColor: "#F5F7FA",
                borderColor: "#1E3A5F",
              }}
              data-testid="diensten-trust-note"
            >
              SovEU geeft geen juridische compliance-stempel en belooft geen automatische naleving. Wij helpen organisaties om AI-gebruik, risico's en documentatie aantoonbaar op orde te brengen.
            </p>
          </FadeIn>
        </div>
      </section>

      {/* Services */}
      <section className="pb-24" data-testid="diensten-services">
        <div className="container-soveu space-y-6">
          <FadeIn className="max-w-3xl mb-6 mt-2">
            <div className="eyebrow mb-3">Bestuurlijke regie</div>
            <h2 className="h2-display mb-4">
              Bestuurlijke regie voor publieke en semipublieke organisaties
            </h2>
            <p className="body-lg text-[#334155] leading-relaxed">
              Van eerste bestuurlijke risicofoto tot structurele borging. Vier producten die aansluiten op de fase waarin uw organisatie zich bevindt.
            </p>
          </FadeIn>
          {services.map((s, i) => (
            <FadeIn key={s.id} delay={i * 60}>
              <article
                id={s.id}
                className="soveu-card p-8 md:p-10 scroll-mt-[110px]"
                data-testid={`service-${s.id}`}
              >
                <div className="grid lg:grid-cols-12 gap-8">
                  <div className="lg:col-span-4">
                    <span
                      className="inline-block text-[11px] font-semibold tracking-[0.14em] uppercase px-2.5 py-1 rounded mb-5"
                      style={{
                        backgroundColor: "#F5F7FA",
                        color: "#2D6F8F",
                        border: "1px solid #E5E9EF",
                      }}
                    >
                      {s.badge}
                    </span>
                    <h2 className="h2-display mb-3">{s.title}</h2>
                    <p
                      className="text-[15px] font-medium mb-5"
                      style={{ color: "#2D6F8F" }}
                    >
                      {s.subtitle}
                    </p>
                    <div
                      className="inline-block text-[14px] font-medium px-3 py-1 rounded-md"
                      style={{ backgroundColor: "#F5F7FA", color: "#1E3A5F" }}
                    >
                      Prijs op aanvraag
                    </div>
                  </div>

                  <div className="lg:col-span-8">
                    <p className="text-[#334155] leading-relaxed body-lg mb-6">
                      {s.description}
                    </p>

                    <div className="text-[15px] font-semibold text-[#1E3A5F] mb-3">
                      Wat u krijgt
                    </div>
                    <ul className="space-y-2 mb-6">
                      {s.includes.map((inc) => (
                        <li
                          key={inc}
                          className="flex items-start gap-3 text-[#334155] text-[15px]"
                        >
                          <Check
                            size={18}
                            className="mt-0.5 flex-shrink-0"
                            style={{ color: "#2D6F8F" }}
                            strokeWidth={2}
                          />
                          <span>{inc}</span>
                        </li>
                      ))}
                    </ul>

                    <div className="border-t border-[#E5E9EF] pt-5 mb-7">
                      <div className="text-[13px] font-semibold tracking-[0.12em] uppercase mb-2" style={{ color: "#1E3A5F" }}>
                        Voor wie
                      </div>
                      <p className="text-[#334155] text-[15px] leading-relaxed">
                        {s.audience}
                      </p>
                    </div>

                    <Link
                      to="/contact"
                      data-testid={`service-cta-${s.id}`}
                      className="btn-primary"
                    >
                      {s.cta}
                      <ArrowRight size={18} />
                    </Link>
                  </div>
                </div>
              </article>
            </FadeIn>
          ))}
        </div>
      </section>

      {/* Closing CTA */}
      <section
        className="section"
        style={{ backgroundColor: "#F5F7FA" }}
        data-testid="diensten-closing"
      >
        <div className="container-soveu max-w-3xl text-center">
          <FadeIn>
            <h2 className="h2-display mb-5">
              Niet zeker welk product past?
            </h2>
            <p className="body-lg text-[#334155] mb-8">
              Plan een kort, vrijblijvend gesprek. We denken mee over de vraag waar uw organisatie nu staat en welke stap logisch is.
            </p>
            <Link to="/contact" className="btn-primary" data-testid="diensten-final-cta">
              Plan een gesprek
              <ArrowRight size={18} />
            </Link>
          </FadeIn>
        </div>
      </section>

      {/* FAQ */}
      <section className="section bg-white" data-testid="diensten-faq">
        <div className="container-soveu max-w-3xl">
          <FadeIn className="mb-8">
            <div className="eyebrow mb-3">Veelgestelde vragen</div>
            <h2 className="h2-display">Vragen over AI Act, AI-governance en MKB</h2>
          </FadeIn>
          <div className="space-y-6">
            {dienstenFaqs.map((q, i) => (
              <FadeIn key={i} delay={i * 60}>
                <article
                  className="border-t border-[#E5E9EF] pt-6"
                  data-testid={`diensten-faq-${i + 1}`}
                >
                  <h3
                    className="text-[18px] font-semibold mb-2"
                    style={{ color: "#1E3A5F" }}
                  >
                    {q.q}
                  </h3>
                  <p className="text-[#334155] leading-relaxed text-[15px]">
                    {q.a}
                  </p>
                </article>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      <InzichtenLinkBlock
        testid="diensten-inzichten"
        title="Veelgestelde bestuurlijke vragen"
        intro="Deze artikelen helpen organisaties bepalen waar zij staan voordat zij een Bestuursscan of Regiebeeld aanvragen."
      />
    </div>
  );
}
